import { useCallback, useEffect, useState } from 'react';

import { Box, Stack } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';

import Button from 'domains/core/components/Button';
import SearchField from 'domains/core/components/SearchField';
import { SettingsSectionHeaderIcon } from 'domains/core/components/SettingsHeader';
import SettingsPageLayout from 'domains/core/components/SettingsPageLayout';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import SuccessSnackbar from 'domains/core/components/Snackbars/SuccessSnackbar';
import TableEmptyState from 'domains/core/components/Table/TableEmptyState';
import TableOffsetPagination from 'domains/core/components/TableOffsetPagination';
import useCreateRole from 'hooks/mutations/useCreateRole';
import useGetRoleById from 'hooks/queries/useGetRoleById';
import useGetRolesList from 'hooks/queries/useGetRolesList';
import { useAccessCentrePermissions } from 'hooks/useAccessCentrePermissions';
import useDebounce from 'hooks/useDebounce';
import { SortDirection, URLPaths } from 'models/enums';
import {
    ACCESS_CENTRE_LIST_PAGE_SIZE,
    ACCESS_CENTRE_ROWS_PER_PAGE_OPTIONS,
    getRoleDetailFromByIdResponse,
    SEARCH_DEBOUNCE_MS,
} from 'utils';

import RoleNameDescriptionDialog from './RoleNameDescriptionDialog/RoleNameDescriptionDialog';
import RolesTable from './RolesTable';

import type { SettingsRolesLocationState } from './settingsRolesLocationState';
import type { AccessCentreRole } from 'domains/accessCentre/types';

const COPY_OF_PREFIX = 'Copy of ';

const RolesPage = () => {
    const { canManageAccessManagement } = useAccessCentrePermissions();
    const history = useHistory();
    const location = useLocation<SettingsRolesLocationState>();
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
    const [duplicateRole, setDuplicateRole] = useState<AccessCentreRole | null>(null);
    const [successSnackbarKey, setSuccessSnackbarKey] = useState(0);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(ACCESS_CENTRE_LIST_PAGE_SIZE);
    const [sortColumnState, setSortColumnState] = useState('last_modified_timestamp');
    const [sortDirectionState, setSortDirectionState] = useState(SortDirection.DESCENDING);

    const offset = (page - 1) * rowsPerPage;

    useEffect(() => {
        setPage(1);
    }, [debouncedSearch]);

    useEffect(() => {
        setPage(1);
    }, [rowsPerPage]);

    const duplicateSourceRoleId = duplicateRole?.id ?? 0;
    const { data: duplicateRoleDetailData, isLoading: isDuplicateRoleDetailLoading } = useGetRoleById(
        duplicateSourceRoleId,
        {
            enabled: duplicateDialogOpen && Boolean(duplicateRole),
        }
    );

    const closeDuplicateDialog = useCallback(() => {
        setDuplicateDialogOpen(false);
        setDuplicateRole(null);
    }, []);

    const {
        mutate: createDuplicateRole,
        isLoading: isDuplicateCreateLoading,
        isError: isDuplicateCreateError,
        error: duplicateCreateError,
        reset: resetDuplicateCreate,
    } = useCreateRole({
        onSuccess: (_data, variables) => {
            closeDuplicateDialog();
            setSuccessMessage(`Role Duplicated. ${variables.name} is ready to be assigned to users`);
            setSuccessSnackbarKey((k) => k + 1);
        },
    });

    const { data, isLoading, isSuccess, isRefetching } = useGetRolesList({
        limit: rowsPerPage,
        offset,
        searchTerm: debouncedSearch.trim() || undefined,
        sortColumn: sortColumnState,
        sortDirection: sortDirectionState,
        reactQueryOptions: {
            keepPreviousData: true,
        },
    });

    const onSort = (sortColumn: string, direction: SortDirection) => {
        setSortColumnState(sortColumn);
        setSortDirectionState(direction);
        setPage(1);
    };

    const roles = data?.data ?? [];
    const total = data?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
    const isInitialLoading = isLoading && data === undefined;

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    useEffect(() => {
        const msg = location.state?.rolesListSuccessMessage;
        if (typeof msg !== 'string' || !msg.trim()) {
            return;
        }
        setSuccessMessage(msg);
        setSuccessSnackbarKey((k) => k + 1);
        history.replace(URLPaths.SETTINGS_ROLES, {});
    }, [location.state?.rolesListSuccessMessage, history]);

    const previousPage = () => {
        if (page > 1) {
            setPage((p) => p - 1);
        }
    };

    const nextPage = () => {
        if (page < totalPages) {
            setPage((p) => p + 1);
        }
    };

    const emptySearch = isSuccess && roles.length === 0 && debouncedSearch.trim().length > 0;

    /** Same pattern as ContentPageLayout: avoid interacting while list query is in flight. */
    const isHeaderControlsDisabled = isLoading || isRefetching;

    const headerOthers = (
        <Stack direction="row" alignItems="center">
            <SearchField
                placeholder="Search roles..."
                value={searchTerm}
                onChange={setSearchTerm}
                disabled={isHeaderControlsDisabled}
                dataTestId="access-center-roles-search-input"
            />
        </Stack>
    );

    const openCreateDialog = () => setCreateDialogOpen(true);
    const closeCreateDialog = () => setCreateDialogOpen(false);

    const handleCreateDialogSubmit = useCallback(
        ({ name, description }: { name: string; description: string }) => {
            const state: SettingsRolesLocationState = {
                createRoleDraft: {
                    name,
                    description: description.trim(),
                },
            };
            history.push({
                pathname: URLPaths.SETTINGS_ROLES,
                search: '?id=default',
                state,
            });
            setCreateDialogOpen(false);
        },
        [history]
    );

    const openDuplicateDialog = useCallback(
        (role: AccessCentreRole) => {
            resetDuplicateCreate();
            setDuplicateRole(role);
            setDuplicateDialogOpen(true);
        },
        [resetDuplicateCreate]
    );

    const duplicateSourceDetail = getRoleDetailFromByIdResponse(duplicateRoleDetailData);
    const duplicatePermissionCount = duplicateSourceDetail?.permissions?.length ?? 0;

    const handleDuplicateDialogSubmit = useCallback(
        ({ name, description }: { name: string; description: string }) => {
            const detail = getRoleDetailFromByIdResponse(duplicateRoleDetailData);
            const permissionIds = detail?.permissions?.map((p) => p.id) ?? [];
            if (permissionIds.length === 0) return;
            createDuplicateRole({
                name,
                description: description.trim() || undefined,
                permissionIds,
            });
        },
        [createDuplicateRole, duplicateRoleDetailData]
    );

    const headerAction = canManageAccessManagement ? (
        <Button
            variant="contained"
            onClick={openCreateDialog}
            disabled={isHeaderControlsDisabled}
            data-testid="access-center-roles-create-role-button"
        >
            Create
        </Button>
    ) : undefined;

    return (
        <Stack data-testid="access-center-roles-page" role="main" aria-label="Users and roles - Roles">
            <SettingsPageLayout
                header="roles"
                headerIcon={<SettingsSectionHeaderIcon />}
                headerOthers={headerOthers}
                {...(headerAction ? { headerAction } : {})}
            >
                <>
                    <RolesTable
                        roles={roles}
                        isLoading={isLoading || isRefetching}
                        showRoleRowActions={canManageAccessManagement}
                        onSort={onSort}
                        onDuplicateRole={canManageAccessManagement ? openDuplicateDialog : undefined}
                    />
                    <RoleNameDescriptionDialog
                        mode="create"
                        open={createDialogOpen}
                        onClose={closeCreateDialog}
                        onPrimary={handleCreateDialogSubmit}
                    />
                    <RoleNameDescriptionDialog
                        mode="duplicate"
                        open={duplicateDialogOpen}
                        onClose={() => {
                            closeDuplicateDialog();
                            resetDuplicateCreate();
                        }}
                        onPrimary={handleDuplicateDialogSubmit}
                        initialName={duplicateRole ? `${COPY_OF_PREFIX}${duplicateRole.name}` : ''}
                        initialDescription={duplicateRole?.description ?? ''}
                        isPrimaryLoading={
                            isDuplicateCreateLoading ||
                            isDuplicateRoleDetailLoading ||
                            (Boolean(duplicateRole) &&
                                !isDuplicateRoleDetailLoading &&
                                duplicateRoleDetailData != null &&
                                duplicatePermissionCount === 0)
                        }
                    />
                    {successMessage && (
                        <SuccessSnackbar
                            key={`${successSnackbarKey}-${successMessage}`}
                            successMessage={successMessage}
                        />
                    )}
                    {isDuplicateCreateError && duplicateCreateError && (
                        <ErrorSnackbar
                            key={
                                duplicateCreateError instanceof Error
                                    ? duplicateCreateError.message
                                    : String(duplicateCreateError)
                            }
                            errorMessage={
                                duplicateCreateError instanceof Error
                                    ? duplicateCreateError.message
                                    : String(duplicateCreateError)
                            }
                        />
                    )}
                    {emptySearch && (
                        <Box data-testid="access-center-roles-empty-search">
                            <TableEmptyState
                                searchTerm={debouncedSearch.trim()}
                                noResultsFoundText="no match found"
                                noResultsFoundSubtitle="Check the spelling or try a different search term"
                            />
                        </Box>
                    )}
                    {!isInitialLoading && !emptySearch && (
                        <TableOffsetPagination
                            page={page}
                            totalPages={totalPages}
                            onPreviousPage={previousPage}
                            onNextPage={nextPage}
                            rowsPerPage={rowsPerPage}
                            rowsPerPageOptions={ACCESS_CENTRE_ROWS_PER_PAGE_OPTIONS}
                            onRowsPerPageChange={setRowsPerPage}
                            testIdPrefix="access-center-roles-pagination"
                        />
                    )}
                </>
            </SettingsPageLayout>
        </Stack>
    );
};

export default RolesPage;
